import { advanceInvoiceIdsSchema, invoiceAccountingKindSchema } from './advance-invoice.js';
import { mailBusinessSendOptionsSchema } from './mail-outbound.js';
import { z } from 'zod';
import { GOODS_RECEIPT_QUALITY_STATUSES, GOODS_RECEIPT_SORT_FIELDS, GOODS_RECEIPT_STATUSES, MATCH_TOLERANCE_ABSOLUTE_MAX, MATCH_TOLERANCE_PERCENT_MAX, PURCHASE_ORDER_ITEM_MAX_COUNT, PURCHASE_ORDER_SORT_FIELDS, PURCHASE_ORDER_STATUSES, PURCHASE_ORDER_TEXT_MAX_LENGTH, PURCHASE_PAYMENT_TERMS_MAX_DAYS, THREE_WAY_MATCH_STATUSES, VENDOR_INVOICE_SORT_FIELDS, VENDOR_INVOICE_STATUSES, } from '../constants/procurement.js';
import { incotermSchema, paymentStatusSchema } from './enums.js';
import { ledgerDateSchema } from './financial-accounting.js';
import { ledgerExchangeRateSchema } from './ledger-payments.js';
import { vendorCreditReviewSchema } from './vendor-credits.js';
import { paginationMetaSchema, paginationQuerySchema, sortOrderSchema } from './pagination.js';
import { currencyCodeSchema, idSchema, isoDateSchema, isoDateTimeSchema, moneySchema, nonNegativeQuantitySchema, quantitySchema, percentSchema, positiveQuantitySchema, } from './common.js';
const optionalText = (max) => z.string().trim().max(max).nullish();
export const purchaseOrderStatusSchema = z.enum(PURCHASE_ORDER_STATUSES);
export const goodsReceiptStatusSchema = z.enum(GOODS_RECEIPT_STATUSES);
export const goodsReceiptQualityStatusSchema = z.enum(GOODS_RECEIPT_QUALITY_STATUSES);
export const vendorInvoiceStatusSchema = z.enum(VENDOR_INVOICE_STATUSES);
export const threeWayMatchStatusSchema = z.enum(THREE_WAY_MATCH_STATUSES);
// ---------------------------------------------------------------------------
// Bestellung
// ---------------------------------------------------------------------------
/** Snapshot der Lieferanschrift; nach dem Versand unabhängig von den Stammdaten. */
export const deliveryAddressSchema = z.object({
    recipient: z.string().trim().min(1).max(200),
    addressLine2: z.string().trim().max(200).nullable(),
    street: z.string().trim().max(200).nullable(),
    houseNumber: z.string().trim().max(20).nullable(),
    postalCode: z.string().trim().max(20).nullable(),
    city: z.string().trim().max(100).nullable(),
    countryCode: z.string().trim().length(2),
    /** Ziel-Lagerbereich, z. B. „Wareneingang Halle 2". */
    storageArea: z.string().trim().max(100).nullable(),
});
export const purchaseOrderItemSchema = z.object({
    id: idSchema,
    position: z.number().int().min(1),
    productId: idSchema.nullable(),
    articleNumber: z.string().nullable(),
    supplierSku: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    quantityOrdered: nonNegativeQuantitySchema,
    quantityDelivered: nonNegativeQuantitySchema,
    quantityBilled: nonNegativeQuantitySchema,
    unit: z.string(),
    purchasePrice: moneySchema,
    taxRate: percentSchema,
    lineNet: moneySchema,
    lineGross: moneySchema,
    /** Noch nicht gelieferte Restmenge; Grundlage der Wareneingangs-Vorbelegung. */
    quantityOpen: nonNegativeQuantitySchema,
    isBatchManaged: z.boolean(),
    isShelfLifeManaged: z.boolean(),
});
export const purchaseOrderItemWriteSchema = z.object({
    id: idSchema.optional(),
    position: z.number().int().min(1).max(9999),
    productId: idSchema.nullish(),
    supplierSku: optionalText(60),
    name: z.string().trim().min(1).max(200),
    description: optionalText(PURCHASE_ORDER_TEXT_MAX_LENGTH),
    quantityOrdered: positiveQuantitySchema,
    unit: z.string().trim().min(1).max(10).default('PCE'),
    purchasePrice: moneySchema,
    taxRate: percentSchema.default('19'),
});
export const purchaseOrderSchema = z.object({
    id: idSchema,
    purchaseOrderNumber: z.string(),
    status: purchaseOrderStatusSchema,
    supplierId: idSchema,
    supplierNumber: z.string(),
    supplierName: z.string(),
    supplierEmail: z.string().nullable(),
    supplierContactId: idSchema.nullable(),
    supplierContactName: z.string().nullable(),
    orderDate: isoDateSchema,
    expectedDeliveryDate: isoDateSchema.nullable(),
    deliveryAddress: deliveryAddressSchema,
    currency: z.string(),
    paymentTermsDays: z.number().int().min(0),
    incoterms: incotermSchema.nullable(),
    notes: z.string().nullable(),
    netTotal: moneySchema,
    taxTotal: moneySchema,
    grossTotal: moneySchema,
    isLocked: z.boolean(),
    sentAt: isoDateTimeSchema.nullable(),
    createdByUserId: idSchema.nullable(),
    createdByName: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const purchaseOrderDetailSchema = purchaseOrderSchema.extend({
    items: z.array(purchaseOrderItemSchema),
    /** Verknüpfte Wareneingänge; Grundlage der Belegkette. */
    goodsReceipts: z.array(z.object({
        id: idSchema,
        receiptNumber: z.string(),
        receivedDate: isoDateSchema,
        status: goodsReceiptStatusSchema,
    })),
    vendorInvoices: z.array(z.object({
        id: idSchema,
        invoiceNumber: z.string(),
        invoiceDate: isoDateSchema,
        status: vendorInvoiceStatusSchema,
        grossAmount: moneySchema,
    })),
});
export const purchaseOrderListItemSchema = purchaseOrderSchema.omit({
    deliveryAddress: true,
    notes: true,
});
export const purchaseOrderCreateSchema = z.object({
    supplierId: idSchema,
    supplierContactId: idSchema.nullish(),
    orderDate: isoDateSchema.optional(),
    expectedDeliveryDate: isoDateSchema.nullish(),
    deliveryAddress: deliveryAddressSchema.partial({ storageArea: true }).nullish(),
    currency: currencyCodeSchema.default('EUR'),
    paymentTermsDays: z.number().int().min(0).max(PURCHASE_PAYMENT_TERMS_MAX_DAYS).optional(),
    incoterms: incotermSchema.nullish(),
    notes: optionalText(PURCHASE_ORDER_TEXT_MAX_LENGTH),
    items: z.array(purchaseOrderItemWriteSchema).min(1).max(PURCHASE_ORDER_ITEM_MAX_COUNT),
});
export const purchaseOrderUpdateSchema = z
    .object({
    supplierContactId: idSchema.nullish(),
    expectedDeliveryDate: isoDateSchema.nullish(),
    deliveryAddress: deliveryAddressSchema.partial({ storageArea: true }),
    paymentTermsDays: z.number().int().min(0).max(PURCHASE_PAYMENT_TERMS_MAX_DAYS),
    incoterms: incotermSchema.nullish(),
    notes: optionalText(PURCHASE_ORDER_TEXT_MAX_LENGTH),
    items: z.array(purchaseOrderItemWriteSchema).min(1).max(PURCHASE_ORDER_ITEM_MAX_COUNT),
})
    .partial()
    .refine((input) => Object.keys(input).length > 0, 'Die Anfrage enthält keine zu speichernden Felder.');
export const purchaseOrderStatusUpdateSchema = z.object({
    status: z.enum(['SENT', 'CONFIRMED']),
});
export const purchaseOrderCancelSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const purchaseOrderEmailSchema = z.object({
    ...mailBusinessSendOptionsSchema.shape,
    to: z.string().trim().email(),
    cc: z.array(z.string().trim().email()).max(5).default([]),
    subject: z.string().trim().min(1).max(200),
    bodyText: z.string().trim().min(1).max(5000),
});
export const purchaseOrderListQuerySchema = paginationQuerySchema.omit({ sort: true }).extend({
    status: z
        .preprocess((value) => (typeof value === 'string' ? value.split(',') : value), z.array(purchaseOrderStatusSchema))
        .optional(),
    supplierId: idSchema.optional(),
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
    sortField: z.enum(PURCHASE_ORDER_SORT_FIELDS).default('orderDate'),
    sortOrder: sortOrderSchema.default('desc'),
});
export const purchaseOrderListResponseSchema = z.object({
    data: z.array(purchaseOrderListItemSchema),
    meta: paginationMetaSchema,
});
// ---------------------------------------------------------------------------
// Wareneingang
// ---------------------------------------------------------------------------
export const goodsReceiptItemSchema = z.object({
    id: idSchema,
    purchaseOrderItemId: idSchema.nullable(),
    position: z.number().int().min(1),
    productId: idSchema,
    articleNumber: z.string(),
    productName: z.string(),
    quantityReceived: nonNegativeQuantitySchema,
    unit: z.string(),
    batchNumber: z.string().nullable(),
    productionDate: isoDateSchema.nullable(),
    expiryDate: isoDateSchema.nullable(),
    warehouseCode: z.string().nullable().default(null),
    storageLocation: z.string().nullable(),
    qualityStatus: goodsReceiptQualityStatusSchema,
    createdBatchId: idSchema.nullable(),
    unitCost: moneySchema.nullable(),
    notes: z.string().nullable(),
});
export const goodsReceiptItemWriteSchema = z.object({
    purchaseOrderItemId: idSchema.nullish(),
    position: z.number().int().min(1).max(9999),
    productId: idSchema,
    quantityReceived: positiveQuantitySchema,
    unit: z.string().trim().min(1).max(10).default('PCE'),
    batchNumber: optionalText(60),
    productionDate: isoDateSchema.nullish(),
    expiryDate: isoDateSchema.nullish(),
    warehouseCode: optionalText(60),
    storageLocation: optionalText(60),
    qualityStatus: goodsReceiptQualityStatusSchema.default('ACCEPTED'),
    unitCost: moneySchema.nullish(),
    notes: optionalText(500),
});
export const goodsReceiptWarehouseLookupsSchema = z.array(z.object({
    code: z.string().min(1),
    storageLocations: z.array(z.string()),
}));
export const goodsReceiptSchema = z.object({
    id: idSchema,
    receiptNumber: z.string(),
    status: goodsReceiptStatusSchema,
    purchaseOrderId: idSchema.nullable(),
    purchaseOrderNumber: z.string().nullable(),
    supplierId: idSchema,
    supplierName: z.string(),
    supplierDeliveryNoteNumber: z.string().nullable(),
    receivedDate: isoDateSchema,
    receivedByUserId: idSchema.nullable(),
    receivedByName: z.string().nullable(),
    storageArea: z.string().nullable(),
    notes: z.string().nullable(),
    /** Speicherschlüssel des Lieferschein-Scans; die Datei liegt außerhalb des Webroots. */
    attachedDeliveryNoteUrl: z.string().nullable(),
    postedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const goodsReceiptDetailSchema = goodsReceiptSchema.extend({
    items: z.array(goodsReceiptItemSchema),
});
export const goodsReceiptCreateSchema = z.object({
    purchaseOrderId: idSchema.nullish(),
    supplierId: idSchema,
    supplierDeliveryNoteNumber: optionalText(60),
    receivedDate: isoDateSchema.optional(),
    storageArea: optionalText(100),
    notes: optionalText(PURCHASE_ORDER_TEXT_MAX_LENGTH),
    items: z.array(goodsReceiptItemWriteSchema).min(1).max(PURCHASE_ORDER_ITEM_MAX_COUNT),
});
export const goodsReceiptUpdateSchema = goodsReceiptCreateSchema
    .omit({ purchaseOrderId: true, supplierId: true })
    .partial()
    .refine((input) => Object.keys(input).length > 0, 'Die Anfrage enthält keine zu speichernden Felder.');
/** Vorbelegung des Wareneingangs aus den offenen Restmengen einer Bestellung. */
export const goodsReceiptDraftSchema = z.object({
    purchaseOrderId: idSchema,
    purchaseOrderNumber: z.string(),
    supplierId: idSchema,
    supplierName: z.string(),
    storageArea: z.string().nullable(),
    items: z.array(z.object({
        purchaseOrderItemId: idSchema,
        position: z.number().int().min(1),
        productId: idSchema.nullable(),
        articleNumber: z.string().nullable(),
        name: z.string(),
        quantityOpen: nonNegativeQuantitySchema,
        unit: z.string(),
        purchasePrice: moneySchema,
        isBatchManaged: z.boolean(),
        isShelfLifeManaged: z.boolean(),
        shelfLifeDays: z.number().int().nullable(),
    })),
});
export const goodsReceiptListQuerySchema = paginationQuerySchema.omit({ sort: true }).extend({
    status: z
        .preprocess((value) => (typeof value === 'string' ? value.split(',') : value), z.array(goodsReceiptStatusSchema))
        .optional(),
    supplierId: idSchema.optional(),
    purchaseOrderId: idSchema.optional(),
    sortField: z.enum(GOODS_RECEIPT_SORT_FIELDS).default('receivedDate'),
    sortOrder: sortOrderSchema.default('desc'),
});
export const goodsReceiptListResponseSchema = z.object({
    data: z.array(goodsReceiptSchema),
    meta: paginationMetaSchema,
});
// ---------------------------------------------------------------------------
// Eingangsrechnung
// ---------------------------------------------------------------------------
export const vendorInvoiceItemSchema = z.object({
    id: idSchema,
    position: z.number().int().min(1),
    purchaseOrderItemId: idSchema.nullable(),
    goodsReceiptItemId: idSchema.nullable(),
    description: z.string(),
    quantity: quantitySchema,
    unitPrice: moneySchema,
    taxRate: percentSchema,
    lineTotal: moneySchema,
});
export const vendorInvoiceItemWriteSchema = z.object({
    position: z.number().int().min(1).max(9999),
    purchaseOrderItemId: idSchema.nullish(),
    goodsReceiptItemId: idSchema.nullish(),
    description: z.string().trim().min(1).max(200),
    quantity: positiveQuantitySchema,
    unitPrice: moneySchema,
    taxRate: percentSchema.default('19'),
});
export const vendorInvoiceSchema = z.object({
    accountingKind: invoiceAccountingKindSchema.default('STANDARD'),
    advanceInvoiceIds: advanceInvoiceIdsSchema.default([]),
    creditSourceInvoiceId: idSchema.nullable().default(null),
    creditReview: vendorCreditReviewSchema.nullable().default(null),
    id: idSchema,
    version: z.number().int().positive(),
    invoiceNumber: z.string(),
    status: vendorInvoiceStatusSchema,
    supplierId: idSchema,
    supplierName: z.string(),
    purchaseOrderId: idSchema.nullable(),
    purchaseOrderNumber: z.string().nullable(),
    invoiceDate: isoDateSchema,
    dueDate: isoDateSchema,
    receivedAt: isoDateTimeSchema,
    invoiceReceivedDate: isoDateSchema.nullable(),
    serviceDate: isoDateSchema.nullable(),
    accountingBookingDate: isoDateSchema.nullable(),
    accountingTaxDate: isoDateSchema.nullable(),
    exchangeRate: ledgerExchangeRateSchema,
    exchangeRateSource: z.string().nullable(),
    netAmount: moneySchema,
    taxAmount: moneySchema,
    grossAmount: moneySchema,
    currency: z.string(),
    paymentStatus: paymentStatusSchema,
    paidAmount: moneySchema,
    cashDiscountTaken: moneySchema,
    cashDiscountPercent: percentSchema,
    cashDiscountDays: z.number().int().min(0),
    /** Letzter Tag, an dem Skonto gezogen werden kann; `null` ohne Skontovereinbarung. */
    cashDiscountDueDate: isoDateSchema.nullable(),
    documentUrl: z.string().nullable(),
    paymentBlockedReason: z.string().nullable(),
    creditorAccountNumber: z.string().nullable(),
    approvedByUserId: idSchema.nullable(),
    approvedByName: z.string().nullable(),
    approvedAt: isoDateTimeSchema.nullable(),
    paidAt: isoDateTimeSchema.nullable(),
    datevExportedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const vendorInvoiceDetailSchema = vendorInvoiceSchema.extend({
    captureSource: z.enum(['MANUAL', 'ELECTRONIC']).default('MANUAL'),
    items: z.array(vendorInvoiceItemSchema),
});
const vendorInvoiceCaptureSupplierSchema = z.object({
    id: idSchema,
    displayName: z.string(),
    partnerNumber: z.string(),
    currency: currencyCodeSchema,
    paymentTermsDays: z.number().int().nullable(),
});
export const vendorInvoiceCaptureQuerySchema = paginationQuerySchema.extend({
    supplierId: idSchema.optional(),
});
export const vendorInvoiceCaptureContextSchema = z.object({
    businessDate: isoDateSchema,
    baseCurrency: currencyCodeSchema,
    data: z.array(vendorInvoiceCaptureSupplierSchema),
    meta: paginationMetaSchema,
    selectedSupplier: vendorInvoiceCaptureSupplierSchema.nullable(),
});
export const vendorInvoiceCreateSchema = z.object({
    accountingKind: invoiceAccountingKindSchema.default('STANDARD'),
    advanceInvoiceIds: advanceInvoiceIdsSchema.default([]),
    invoiceNumber: z.string().trim().min(1).max(60),
    supplierId: idSchema,
    purchaseOrderId: idSchema.nullish(),
    invoiceDate: ledgerDateSchema,
    invoiceReceivedDate: ledgerDateSchema.nullish(),
    serviceDate: ledgerDateSchema.nullish(),
    accountingBookingDate: ledgerDateSchema.nullish(),
    accountingTaxDate: ledgerDateSchema.nullish(),
    exchangeRate: ledgerExchangeRateSchema.default('1'),
    exchangeRateSource: z.string().trim().min(3).max(240).nullish(),
    dueDate: ledgerDateSchema.optional(),
    currency: currencyCodeSchema.default('EUR'),
    cashDiscountPercent: percentSchema.default('0'),
    cashDiscountDays: z.number().int().min(0).max(PURCHASE_PAYMENT_TERMS_MAX_DAYS).default(0),
    items: z.array(vendorInvoiceItemWriteSchema).min(1).max(PURCHASE_ORDER_ITEM_MAX_COUNT),
});
export const vendorInvoiceUpdateSchema = z
    .object({
    accountingKind: invoiceAccountingKindSchema,
    advanceInvoiceIds: advanceInvoiceIdsSchema,
    invoiceNumber: z.string().trim().min(1).max(60),
    invoiceDate: ledgerDateSchema,
    invoiceReceivedDate: ledgerDateSchema.nullish(),
    serviceDate: ledgerDateSchema.nullish(),
    accountingBookingDate: ledgerDateSchema.nullish(),
    accountingTaxDate: ledgerDateSchema.nullish(),
    exchangeRate: ledgerExchangeRateSchema,
    exchangeRateSource: z.string().trim().min(3).max(240).nullish(),
    dueDate: ledgerDateSchema,
    purchaseOrderId: idSchema.nullish(),
    cashDiscountPercent: percentSchema,
    cashDiscountDays: z.number().int().min(0).max(PURCHASE_PAYMENT_TERMS_MAX_DAYS),
    items: z.array(vendorInvoiceItemWriteSchema).min(1).max(PURCHASE_ORDER_ITEM_MAX_COUNT),
})
    .partial()
    .extend({ version: z.number().int().positive() })
    .refine((input) => Object.keys(input).some((name) => name !== 'version'), 'Die Anfrage enthält keine zu speichernden Felder.');
/** Source amounts and line evidence of an electronic invoice are deliberately absent. */
export const vendorInvoiceMetadataUpdateSchema = z
    .object({
    version: z.number().int().positive(),
    invoiceReceivedDate: ledgerDateSchema.nullish(),
    serviceDate: ledgerDateSchema.nullish(),
    accountingBookingDate: ledgerDateSchema.nullish(),
    accountingTaxDate: ledgerDateSchema.nullish(),
    dueDate: ledgerDateSchema,
    exchangeRate: ledgerExchangeRateSchema,
    exchangeRateSource: z.string().trim().min(3).max(240).nullish(),
    cashDiscountPercent: percentSchema,
    cashDiscountDays: z.number().int().min(0).max(PURCHASE_PAYMENT_TERMS_MAX_DAYS),
})
    .strict();
export const vendorInvoiceApproveSchema = z.object({
    /** Begründung, wenn trotz offener Abweichung freigegeben wird. */
    overrideReason: z.string().trim().min(3).max(500).nullish(),
    version: z.number().int().positive().optional(),
    previewHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    idempotencyKey: z.string().trim().min(8).max(100).optional(),
});
export const vendorInvoiceCancelSchema = z.object({
    version: z.number().int().positive(),
    previewHash: z.string().regex(/^[a-f0-9]{64}$/u),
    bookingDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
    evidenceReference: z.string().trim().min(10).max(1000),
    idempotencyKey: z.string().trim().min(8).max(100),
});
export const vendorInvoiceCancellationPreviewSchema = z.object({
    id: idSchema,
    invoiceNumber: z.string(),
    version: z.number().int().positive(),
    booked: z.boolean(),
    eligible: z.boolean(),
    blockedReason: z
        .enum([
        'vendorInvoiceCancelled',
        'vendorInvoiceHasSettlements',
        'adoptionRequired',
        'cannotReverse',
    ])
        .nullable(),
    businessDate: ledgerDateSchema,
    minimumBookingDate: ledgerDateSchema.nullable(),
    taxDate: ledgerDateSchema.nullable(),
    currency: currencyCodeSchema,
    grossAmount: moneySchema,
    originalJournalId: idSchema.nullable(),
    correctionJournalId: idSchema.nullable(),
    correction: z
        .object({
        bookingDate: ledgerDateSchema,
        taxDate: ledgerDateSchema.nullable(),
        reason: z.string(),
        evidenceReference: z.string(),
        recordedAt: isoDateTimeSchema,
        actorName: z.string().nullable(),
    })
        .nullable()
        .default(null),
    previewHash: z.string().regex(/^[a-f0-9]{64}$/u),
});
export const vendorInvoiceComplaintSchema = z.object({
    ...mailBusinessSendOptionsSchema.shape,
    to: z.string().trim().email(),
    subject: z.string().trim().min(1).max(200),
    bodyText: z.string().trim().min(1).max(5000),
});
export const vendorInvoiceListQuerySchema = paginationQuerySchema.omit({ sort: true }).extend({
    status: z
        .preprocess((value) => (typeof value === 'string' ? value.split(',') : value), z.array(vendorInvoiceStatusSchema))
        .optional(),
    supplierId: idSchema.optional(),
    dueFrom: isoDateSchema.optional(),
    dueTo: isoDateSchema.optional(),
    sortField: z.enum(VENDOR_INVOICE_SORT_FIELDS).default('dueDate'),
    sortOrder: sortOrderSchema.default('asc'),
});
export const vendorInvoiceListResponseSchema = z.object({
    data: z.array(vendorInvoiceSchema),
    meta: paginationMetaSchema,
    summary: z.object({
        openGross: moneySchema,
        overdueGross: moneySchema,
        discrepancyCount: z.number().int().min(0),
        cashDiscountExpiringCount: z.number().int().min(0),
    }),
});
// ---------------------------------------------------------------------------
// 3-Wege-Abgleich
// ---------------------------------------------------------------------------
export const matchToleranceSchema = z.object({
    absoluteAmount: moneySchema.refine((value) => Number(value) >= 0 && Number(value) <= Number(MATCH_TOLERANCE_ABSOLUTE_MAX), `Die absolute Toleranz muss zwischen 0 und ${MATCH_TOLERANCE_ABSOLUTE_MAX} liegen.`),
    percent: percentSchema.refine((value) => Number(value) <= Number(MATCH_TOLERANCE_PERCENT_MAX), `Die relative Toleranz darf höchstens ${MATCH_TOLERANCE_PERCENT_MAX} % betragen.`),
});
export const threeWayMatchLineSchema = z.object({
    position: z.number().int().min(1).nullable(),
    description: z.string(),
    purchaseOrderItemId: idSchema.nullable(),
    goodsReceiptItemId: idSchema.nullable(),
    batchNumbers: z.array(z.string()),
    quantityOrdered: nonNegativeQuantitySchema,
    quantityDelivered: nonNegativeQuantitySchema,
    quantityInvoiced: quantitySchema,
    priceOrdered: moneySchema,
    priceInvoiced: moneySchema,
    quantityVariance: z.string(),
    priceVariance: z.string(),
    amountVariance: z.string(),
    status: threeWayMatchStatusSchema,
});
export const threeWayMatchResultSchema = z.object({
    vendorInvoiceId: idSchema,
    invoiceStatus: vendorInvoiceStatusSchema,
    purchaseOrderId: idSchema.nullable(),
    purchaseOrderNumber: z.string().nullable(),
    goodsReceiptIds: z.array(idSchema),
    currency: z.string(),
    tolerances: matchToleranceSchema,
    lines: z.array(threeWayMatchLineSchema),
    totalVariance: z.string(),
    matchedCount: z.number().int().min(0),
    toleranceCount: z.number().int().min(0),
    discrepancyCount: z.number().int().min(0),
    isClear: z.boolean(),
    paymentBlockedReason: z.string().nullable(),
});
export const threeWayMatchLogSchema = z.object({
    id: idSchema,
    vendorInvoiceId: idSchema,
    purchaseOrderId: idSchema.nullable(),
    goodsReceiptId: idSchema.nullable(),
    qtyOrdered: nonNegativeQuantitySchema,
    qtyDelivered: nonNegativeQuantitySchema,
    qtyInvoiced: quantitySchema,
    priceOrdered: moneySchema,
    priceInvoiced: moneySchema,
    qtyVariance: z.string(),
    priceVariance: z.string(),
    matchStatus: threeWayMatchStatusSchema,
    overrideReason: z.string().nullable(),
    reviewedByUserId: idSchema.nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const threeWayMatchLogListSchema = z.object({ data: z.array(threeWayMatchLogSchema) });
export const matchOverrideSchema = z.object({
    overrideReason: z.string().trim().min(3).max(500),
});
export const procurementUploadResponseSchema = z.object({
    storageKey: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().min(0),
});
//# sourceMappingURL=procurement.js.map